'use client';

import { DialogContainer } from '#/components/dialog-kit';
import { Toaster } from '#/components/ui/sonner';
import { useLocaleStore } from '#/contexts/locale-store';
import * as Sentry from '@sentry/nextjs';
import consolev from 'consolev';
import { IntlProvider } from 'intl-lib';
import { ReactNode, useEffect } from 'react';
import en_US from './dictionaries/en-US.json';
import { MarkdownRenderer } from './renderers/markdown-renderer';

type TranslationTypeImpl = typeof en_US;

declare module 'intl-lib' {
  interface TranslationType extends TranslationTypeImpl {}
}

Sentry.init({
  dsn: 'https://9e0a65decd1d4f68bb80f1d62989edae@o1325261.ingest.sentry.io/6584355',

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,

  enabled: process.env.NODE_ENV === 'production',
  environment: process.env.NEXT_PUBLIC_CHAIN_ID,
  release: process.env.NEXT_PUBLIC_VERSION,
});

export function Provider({ children }: { children: ReactNode }) {
  const locale = useLocaleStore(state => state.locale);

  useEffect(() => {
    consolev(
      `${process.env.NEXT_PUBLIC_NAME}@${process.env.NEXT_PUBLIC_VERSION}`,
      process.env.NEXT_PUBLIC_SHA
    );
  }, []);

  return (
    <IntlProvider
      defaultLocale="en-US"
      locale={locale}
      dictionaries={{
        'en-US': en_US,
        'zh-CN': () =>
          import('./dictionaries/zh-CN.json').then(module => module.default),
      }}
      renderers={{
        markdown: MarkdownRenderer,
      }}
    >
      {children}
      <Toaster />
      <DialogContainer />
    </IntlProvider>
  );
}
